import React, { ReactNode, useMemo, useRef } from 'react';
import { Modal, ModalProps } from '@material-ui/core';
import classes from './styles.module.css';
import AppBar from './AppBar';
import { useIsTabletOrMobileScreenSize } from '@app/utils/media-query';
import cls from 'classnames';
import { useEffect } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import { removeHashOfUrl } from '@app/utils/routing';

type AppBarConfig = {
  title?: string;
  color?: string;
  tintColor?: string;
  closeButtonClickCustomHandler?: () => void;
  backButtonClickCustomHandler?: () => void;
};

type Props = ModalProps & {
  appBarConfig?: AppBarConfig;
  desktopWidthContent: string | number;
  desktopHeightContent: string | number;
  desktopMinHeightContent?: string | number;
  desktopMaxHeightContent?: string | number;
  hiddenDesktopBar?: boolean;
  hiddenMobileBar?: boolean;
  AppBarRightSide?: ReactNode;
  children: ReactNode;
};

function emptyFunction() {
  return;
}

export default function ModalResponsive({
  appBarConfig,
  desktopHeightContent,
  desktopWidthContent,
  desktopMaxHeightContent,
  desktopMinHeightContent,
  hiddenDesktopBar,
  hiddenMobileBar = false,
  AppBarRightSide,
  ...modalProps
}: Props) {
  const isTableOrMobile = useIsTabletOrMobileScreenSize();

  const marginTop = isTableOrMobile ? 0 : 50;

  const shouldUseAppBar = Boolean((isTableOrMobile && !hiddenMobileBar) || !hiddenDesktopBar);

  function dispatchOnClose() {
    const closeFunction = (modalProps.onClose ?? emptyFunction) as () => void;
    closeFunction();
  }

  return (
    <Modal
      {...modalProps}
      className={cls(classes.modal, modalProps.className)}
      style={{ marginTop, ...modalProps.style }}
      onClose={dispatchOnClose}
    >
      <>
        {shouldUseAppBar && (
          <AppBar
            mobile={{ ...appBarConfig }}
            desktop={{ contentWidth: desktopWidthContent }}
            onBackButtonClick={dispatchOnClose}
            onCloseButtonClick={dispatchOnClose}
            RightSide={AppBarRightSide}
          />
        )}
        <div
          className={isTableOrMobile ? classes.contentMobile : classes.contentDesktop}
          style={
            isTableOrMobile
              ? {}
              : {
                  width: desktopWidthContent,
                  height: desktopHeightContent,
                  minHeight: desktopMinHeightContent,
                  maxHeight: desktopMaxHeightContent,
                }
          }
        >
          {modalProps.children}
        </div>
      </>
    </Modal>
  );
}

type Options = {
  onHashChange?: (event: HashChangeEvent) => boolean;
};

const DEFAULT_OPTIONS: Options = {
  onHashChange: undefined,
};

export function useModalWithHashingControl(hash: string, options: Options = DEFAULT_OPTIONS) {
  const [state, setState] = useState(false);

  useEffect(() => {
    function handleHashChange(event: HashChangeEvent) {
      let nextState = false;
      if (options.onHashChange) {
        nextState = options.onHashChange(event);
      } else {
        nextState = window.location.hash === `#${hash}`;
      }
      setState(nextState);
    }

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const open = useCallback(() => {
    removeHashOfUrl(hash);
    window.location.hash = hash;
  }, [hash]);

  const close = useCallback(() => {
    window.history.back();
  }, []);

  return { state: state, open, close };
}
